/**
 * The five work destinations, and the one list both menus read from.
 *
 * Order, labels and icons follow native exactly: Início, Pastas, Rotinas,
 * Contatos, Achar. Activity is deliberately absent — it is a header button
 * there and here, not a sixth tab.
 *
 * The phone renders these as `MobileTabBar` and the desktop as `WorkSidebar`,
 * from this one definition. That is the point: a destination added here reaches
 * both breakpoints, so the phone can no longer drift ahead of the desktop the
 * way it did when the footer menu was declared separately.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { folderOpen, home, person, repeatOutline, search } from 'ionicons/icons';
import { HOME_PATH } from '../components/useHasHome';
import { ROUTINES_PATH } from './entityLinks';

export const BUCKETS_PATH = '/buckets';
export const CONTACTS_PATH = '/contacts';
export const FIND_PATH = '/find';
export const ACTIVITY_PATH = '/activity';
export const SETTINGS_PATH = '/settings';

export type WorkTabKey = 'home' | 'buckets' | 'routines' | 'assigned' | 'find';

export interface WorkTab {
  key: WorkTabKey;
  label: string;
  /** Ionicons data-URL from `ionicons/icons`, as the rest of the shell uses. */
  icon: string;
  /** Native mixes solid and outline glyphs here; `IonIcon` needs to be told. */
  filled: boolean;
  href: string;
}

const SHAPE: Omit<WorkTab, 'label'>[] = [
  { key: 'home', icon: home, filled: true, href: HOME_PATH },
  { key: 'buckets', icon: folderOpen, filled: true, href: BUCKETS_PATH },
  { key: 'routines', icon: repeatOutline, filled: false, href: ROUTINES_PATH },
  { key: 'assigned', icon: person, filled: true, href: CONTACTS_PATH },
  { key: 'find', icon: search, filled: true, href: FIND_PATH },
];

export function useWorkTabs(): WorkTab[] {
  const { t } = useTranslation('web');
  return useMemo(
    () => SHAPE.map((tab) => ({ ...tab, label: t(`tabs.${tab.key}`) })),
    [t],
  );
}
